import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { useRxData } from "rxdb-hooks";
import type { EpocusReferral } from "../../../../global-providers/rxdb-provider/collections/epocus-referral";
import {
  type EpocusReferralQueryConstructor,
  RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
} from "../../../../global-providers/rxdb-provider/collections/epocus-referral";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { ALL_VIEWERS, useDemoViewer } from "../../_lib/use-demo-viewer";
import { linkToEpocusReferralDetail } from "../../links";
import { HARDCODED_RECEIVERS } from "../_lib/hardcoded-receivers";
import { ReferralStatusChip } from "../_lib/referral-status-chip";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 680,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const ReferralCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 10,
  padding: "14px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  cursor: "pointer",
  minHeight: 64,
  "&:active": { opacity: 0.7 },
  "&:hover": { backgroundColor: "#f9fafb" },
});

const URGENCY_COLORS: Record<string, string> = {
  routine: "#6b7280",
  urgent: "#d97706",
  emergency: "#dc2626",
};

const URGENCY_LABELS: Record<string, string> = {
  routine: "Routine",
  urgent: "Urgent",
  emergency: "Emergency",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const ReferralRow: FC<{ referral: EpocusReferral }> = ({ referral }) => {
  const navigate = useNavigate();
  const sender = HARDCODED_RECEIVERS.find(
    (r) => r.id === referral.referringUserId,
  );
  const senderLabel = sender?.name ?? "A clinician";

  return (
    <ReferralCard
      onClick={() => navigate(linkToEpocusReferralDetail(referral.id))}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ")
          navigate(linkToEpocusReferralDetail(referral.id));
      }}
    >
      <Box flex={1} minWidth={0}>
        <Box
          display="flex"
          alignItems="center"
          style={{ gap: 8, marginBottom: 4 }}
        >
          <Typography
            style={{
              fontSize: rem(13),
              fontWeight: 700,
              color: URGENCY_COLORS[referral.urgency],
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            {URGENCY_LABELS[referral.urgency]}
          </Typography>
          <ReferralStatusChip status={referral.status} />
        </Box>
        <Typography
          style={{
            fontSize: rem(15),
            fontWeight: 600,
            color: EPOCUS_TEXT_PRIMARY,
          }}
          noWrap
        >
          {referral.clinicalReason}
        </Typography>
        <Typography
          variant="body2"
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginTop: 2,
          }}
        >
          From {senderLabel} · {formatDate(referral.sentAt)}
        </Typography>
      </Box>
      <Typography
        style={{ color: EPOCUS_TEXT_SECONDARY, fontSize: rem(18) }}
        aria-hidden
      >
        ›
      </Typography>
    </ReferralCard>
  );
};

export const EpocusReferralsInboxPage: FC = () => {
  const viewerId = useDemoViewer();

  const query: EpocusReferralQueryConstructor = (col) =>
    viewerId === ALL_VIEWERS
      ? col?.find({ selector: { status: { $in: ["sent", "acknowledged"] } } })
      : col?.find({
          selector: {
            receivingUserId: { $eq: viewerId ?? "" },
            status: { $in: ["sent", "acknowledged"] },
          },
        });

  const { result: rawReferrals, isFetching } = useRxData<EpocusReferral>(
    RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
    query,
  );
  const referrals = [...(rawReferrals ?? [])].sort((a, b) =>
    b.sentAt.localeCompare(a.sentAt),
  );

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageRoot>
      <Typography
        variant="h5"
        style={{
          fontWeight: 800,
          fontSize: rem(22),
          color: EPOCUS_TEXT_PRIMARY,
        }}
      >
        Referral Inbox
      </Typography>

      {referrals.length === 0 ? (
        <Box
          p={3}
          style={{
            backgroundColor: EPOCUS_WHITE,
            border: `1px solid ${EPOCUS_BORDER}`,
            borderRadius: 10,
            textAlign: "center",
          }}
        >
          <Typography
            style={{
              color: EPOCUS_TEXT_SECONDARY,
              fontSize: rem(15),
              fontStyle: "italic",
            }}
          >
            No pending referrals.
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
          {referrals.map((r) => (
            <ReferralRow key={r.id} referral={r} />
          ))}
        </Box>
      )}
    </PageRoot>
  );
};
